/**
 * @file schemas/m3.ts
 * @description M3 驗證決定+證書+TC 前後端共用 Zod Schema。
 *
 * 涵蓋：
 * - 驗證決定安排、審議表單儲存、成員簽署、總經理核定、手動終結
 * - 證書核發、寄送記錄、寄送確認
 * - TC 文件建立
 * - AI 諮詢服務觸發
 */
import { z } from 'zod';
import { DECISION_CONCLUSIONS, CERT_TYPES } from '../decision-form';
// ─── 驗證決定 ──────────────────────────────────────────────────────────────────
/**
 * 安排審議：技術委員會主委/代表指派恰好 2 位決定小組成員（decider），
 * 選擇決議方式（書面/會議）。會議方式須附會議時間。
 */
export const arrangeDecisionSchema = z
    .object({
    /** 本次審議的 2 位小組成員用戶 ID（恰好 2 位 decider） */
    memberIds: z.array(z.number().int().positive()).length(2, '每次審議需指派恰好 2 位成員'),
    /** 決議方式 */
    reviewMethod: z.enum(['書面', '會議'], { error: '請選擇決議方式' }),
    /** 會議時間（會議方式必填，ISO 格式） */
    scheduledAt: z.string().datetime().optional(),
})
    .refine((v) => v.reviewMethod !== '會議' || !!v.scheduledAt, {
    message: '會議方式需提供會議時間',
    path: ['scheduledAt'],
});
/**
 * 儲存 ZHSR07 表單內容（審議成員填寫）。
 * formContent 為按 scheme 結構化的 checklist 答案 map；其餘為關鍵結構化欄位。
 */
export const saveDecisionFormSchema = z.object({
    /** 審議記錄（自由文字） */
    deliberationNotes: z.string().max(10000).optional(),
    /** 一~三節 checklist 答案 + 集團驗證（{ sectionKey: { itemKey: result | { result, note } } }） */
    formContent: z.record(z.string(), z.unknown()),
    /** 五、結論（DECISION_CONCLUSIONS 之一） */
    conclusion: z.enum(DECISION_CONCLUSIONS).optional(),
    /** 結論「其他」說明 */
    conclusionNote: z.string().max(2000).optional(),
    /** 風險評估等級 */
    riskLevel: z.enum(['低', '中', '高']).optional(),
    /** 追蹤對策（結構化 JSON，前端自定形狀） */
    followUpActions: z.unknown().optional(),
    /** 四、證書資訊（各欄位字串 map，含 firstAuditDate/validFrom/validUntil/certNo） */
    certInfo: z.record(z.string(), z.unknown()).optional(),
});
/**
 * 審議成員簽署（雙簽齊全 → 待總經理核定）。
 */
export const signDecisionSchema = z.object({
    /** 個別意見（可選） */
    opinion: z.string().max(2000).optional(),
});
/**
 * 總經理核定（硬門檻）。同意 → 終結+視結論自動發證；其他 → 退回審議中重編。
 */
export const gmApproveDecisionSchema = z
    .object({
    gmDecision: z.enum(['同意', '其他'], { error: '請選擇核定意見' }),
    gmNote: z.string().max(2000).optional(),
})
    .refine((v) => v.gmDecision !== '其他' || !!v.gmNote?.trim(), {
    message: '核定「其他」需附說明',
    path: ['gmNote'],
});
/**
 * 手動終結決定（admin override）。
 */
export const finalizeDecisionSchema = z.object({
    /** 最終決定 */
    finalDecision: z.enum(['通過', '不通過', '延議'], { error: '請選擇最終決定' }),
    /** 會議紀錄（可選） */
    meetingMinutes: z.string().max(5000).optional(),
});
// ─── 稽核文件包（移交快照）與初審退回 ──────────────────────────────────────────
/** 文件包內單一案件附件項（來源 case_documents join files） */
export const dossierFileItemSchema = z.object({
    fileId: z.number(),
    docType: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
    note: z.string().nullable(),
    uploadedAt: z.string(),
});
/**
 * 稽核文件包快照。報告核准（終稿）自動移交審議小組時，在同事務內彙集
 * 案件全部文檔與稽核活動事實寫入 verification_decisions.dossier_snapshot，
 * 供審議小組初審時直接查閱（決定詳情頁「稽核文件包」卡片）。
 */
export const dossierSnapshotSchema = z.object({
    /** 移交（快照）時間 ISO */
    snapshotAt: z.string(),
    /** 終稿報告指針 */
    report: z.object({
        reportId: z.number(),
        versionId: z.number(),
        version: z.number(),
        code: z.string(),
        finalizedAt: z.string(),
    }),
    /** 案件附件清單 */
    files: z.array(dossierFileItemSchema),
    /** 稽核任務摘要 */
    auditTasks: z.array(z.object({
        id: z.number(),
        assigneeName: z.string().nullable(),
        isTeamLead: z.boolean(),
        status: z.string(),
        arrivedAt: z.string().nullable(),
        leftAt: z.string().nullable(),
    })),
    /** 稽核記錄摘要（現場查核表提交） */
    auditRecords: z.array(z.object({
        id: z.number(),
        formInstanceId: z.number(),
        submittedAt: z.string(),
    })),
    /** 不符合事項清單 */
    ncFindings: z.array(z.object({
        id: z.number(),
        severity: z.string(),
        description: z.string(),
        closed: z.boolean(),
    })),
    /** 最新風險評估（無則 null） */
    riskAssessment: z
        .object({
        riskScore: z.number(),
        recommendation: z.string().nullable(),
        assessedAt: z.string(),
    })
        .nullable(),
});
/**
 * 審議小組初審退回稽核報告（決定 → 已退回、報告 → 草稿、案件 → 報告中）。
 */
export const returnDecisionReportSchema = z.object({
    /** 退回理由（必填，會寫入報告版本退回備註並通知主導稽核員） */
    reason: z.string().trim().min(1, '請填寫退回理由').max(2000),
});
// ─── 證書 ──────────────────────────────────────────────────────────────────────
/**
 * 證書附表明細單列（序 38：檢驗報告判定通過 / 稽核結案時確認的「證書附表」）。
 * 由案件申請明細 / 驗證場所 / 產品帶入預填，可編輯確認。
 */
export const certAnnexItemSchema = z.object({
    /** 產品品項 / 驗證範圍名稱 */
    product: z.string().trim().min(1, '請填寫產品品項').max(200),
    /** 產品類別（可選，如 蔬菜 / 水果） */
    category: z.string().trim().max(100).optional(),
    /** 場所 / 生產地（可選，驗證場所地址或名稱） */
    site: z.string().trim().max(300).optional(),
    /** 面積（公頃，字串承載避免浮點；可選） */
    area: z.string().trim().max(30).optional(),
    /** 備註（可選） */
    note: z.string().trim().max(500).optional(),
});
/**
 * 證書附表（cert annex）。存於 certificates.cert_annex（jsonb）。
 * confirmed=true 表示已於檢驗通過 / 稽核結案時確認，連同稽核文件反饋管理組。
 */
export const certAnnexSchema = z.object({
    /** 附表明細列 */
    items: z.array(certAnnexItemSchema).default([]),
    /** 附表整體備註 */
    remark: z.string().trim().max(2000).optional(),
    /** 是否已確認（檢驗通過 / 稽核結案時） */
    confirmed: z.boolean().default(false),
    /** 確認人 ID */
    confirmedBy: z.number().int().positive().optional(),
    /** 確認時間（ISO） */
    confirmedAt: z.string().optional(),
});
/**
 * 確認 / 編輯證書附表（PATCH /api/certificates/:id/annex）。
 * confirm=true 時落確認人 / 時間並反饋管理組。
 */
export const confirmCertAnnexSchema = z
    .object({
    /**
     * 附表明細列（覆蓋式寫入）。
     * 2026-09-25 回報序49：「儲存草稿」（confirm=false）隨時可存，允許 0 列；
     * 「確認並反饋管理組」（confirm=true）才要求至少一列（見下方 refine）。
     */
    items: z
        .array(certAnnexItemSchema.extend({ product: z.string().trim().max(200).default('') }))
        .default([]),
    /** 附表整體備註 */
    remark: z.string().trim().max(2000).optional(),
    /** 是否確認（true = 檢驗通過 / 稽核結案確認，反饋管理組） */
    confirm: z.boolean().default(false),
})
    .refine((v) => !v.confirm || v.items.length > 0, {
    message: '證書附表至少需一列明細',
    path: ['items'],
})
    // 確認時每列都必須填寫產品品項（草稿允許暫缺）
    .refine((v) => !v.confirm || v.items.every((it) => it.product.length > 0), {
    message: '請填寫產品品項',
    path: ['items'],
});
/**
 * 手動核發證書（或由 decision.finalize 自動觸發）。
 */
export const issueCertificateSchema = z.object({
    /** 關聯案件 ID */
    caseId: z.number().int().positive(),
    /** 驗證產品範圍（JSON 陣列） */
    productScope: z
        .array(z.object({
        product: z.string().min(1),
        area: z.string().optional(),
        unit: z.string().optional(),
    }))
        .min(1, '至少需要一個驗證產品'),
    /** 有效起始日期（ISO 格式） */
    validFrom: z.string().datetime(),
    /** 有效截止日期（ISO 格式） */
    validUntil: z.string().datetime(),
    /**
     * 證書類型（7 種範本之一，序 38）。可選：
     * 未指定時由發證服務依 scheme / 選項 / 類別自動推斷（resolveCertType）。
     */
    certType: z.enum(CERT_TYPES).optional(),
    /** 證書附表（可選；發證時可一併帶入） */
    certAnnex: certAnnexSchema.optional(),
});
/** 製證階段可人工修正的文件欄位。 */
export const updateCertificateDocumentSchema = z.object({
    certDocNo: z.string().trim().min(1).max(30),
    registrationDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD'),
    remark: z.string().trim().max(2000).optional(),
    changeNote: z.string().trim().max(2000).optional(),
    certType: z.enum(CERT_TYPES),
});
/**
 * 記錄證書寄送（shipped）。
 */
export const recordDeliverySchema = z.object({
    /** 寄送方式 */
    deliveryMethod: z.enum(['registered_mail', 'courier', 'pickup'], {
        error: '請選擇寄送方式',
    }),
    /** 快遞追蹤號碼（courier 方式需要） */
    trackingNumber: z.string().max(100).optional(),
    /** 收件人姓名 */
    recipientName: z.string().min(1, '收件人姓名不可為空').max(100),
    /** 備注 */
    notes: z.string().max(500).optional(),
});
/**
 * 確認收到寄送（received confirmation）。
 */
export const confirmDeliverySchema = z.object({
    /** 實際收到時間（ISO 格式，可選，預設 now） */
    receivedAt: z.string().datetime().optional(),
    /** 備注 */
    notes: z.string().max(500).optional(),
});
// ─── TC 文件 ──────────────────────────────────────────────────────────────────
/**
 * 建立 TC 文件。
 */
export const createTcDocumentSchema = z.object({
    /** 關聯證書 ID */
    certificateId: z.number().int().positive(),
    /** 交易類型 */
    transactionType: z.enum(['生產', '加工', '分裝', '流通'], {
        error: '請選擇交易類型',
    }),
    /** 產品項目名稱 */
    productItem: z.string().min(1, '產品名稱不可為空').max(200),
    /** 數量（正數） */
    quantity: z.number().positive('數量必須大於 0').finite('數量必須是有限數值'),
    /** 單位 */
    unit: z.string().min(1, '單位不可為空').max(20),
    /** 交易日期（ISO 格式） */
    transactionDate: z.string().datetime(),
    /** 買受方名稱 */
    buyerName: z.string().min(1, '買受方名稱不可為空').max(200),
    /** 買受方統一編號（可選） */
    buyerTaxId: z.string().max(20).optional(),
    /** 備注 */
    notes: z.string().max(1000).optional(),
});
// ─── AI 諮詢 ──────────────────────────────────────────────────────────────────
/**
 * 提交 AI 諮詢任務（觸發 job queue）。
 */
export const submitConsultancySchema = z.object({
    /** 用戶問題 */
    question: z.string().min(1, '問題不可為空').max(2000),
    /** 可選的附件文件 ID 列表 */
    attachmentFileIds: z.array(z.number().int().positive()).optional().default([]),
    /** 可選的關聯案件 ID */
    caseId: z.number().int().positive().optional(),
});
